import { StatusCode } from "api/protocols";
import { GetStaffAccountShiftsAll, PutStaffAccountShift, GetShifts, PostStaffAccountShift, DeleteStaffAccountShift } from "api/rpc/2024-04/facilityAdmin/client/shifts";
import axios, { CancelToken } from "axios";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError, showSuccess } from "redux/actions/ui";
import { IStaffAccountShift } from "redux/reducers/models/shift";
import "./staffAccountShiftTable.scss";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import TimePick from "components/timePick/TimePick";
import TextField from "components/form/textField/TextField";
import DropFilter from "components/dropdown/DropFilter";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faFileInvoiceDollar,
  faMagnifyingGlassDollar,
  faPen,
  faPlus,
  faTrashCan,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";
import Callout from "components/callout/Callout";
import { Badge } from "components/badge/Badge";
import Popup from "components/popup/Popup";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import SelectNew from "components/select/SelectNew";
import { TFunction } from "i18next";
import { updateFacilityTeeSheetSettings } from "redux/actions/facility";
import { TClockRoundOption } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import Input from "components/form/input";
import { IFacilityStaff } from "redux/reducers/models/facility";
import { getOpenTablesForUser } from "helpers/openTables";
import OpenTablesWarningPopup from "components/openTablesWarning/OpenTablesWarningPopup";
import { ITable } from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { IFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

interface IProps {
  userId?: number;
  addNotes?: boolean;
  allowCreate?: boolean;
  allowDelete?: boolean;
  staffMembers?: IFacilityStaff[];
}

const TABLE_LIMIT = 25;

const SHIFT_USERS_FILTER_KEY = "shift_users_filters";
const SHIFT_USERS_DATE_KEY = "shift_users_date_filters";

type StaffAccountShiftsFilter = {
  user_ids: Array<number>;
  start_date: string;
  end_date: string;
};

const TIME_CLOCK_SETTING_OPTIONS = (t: TFunction) => [
  { id: "no_round", label: t("Don't Round") },
  { id: "round_5", label: t("5 Minutes") },
  { id: "round_10", label: t("10 Minutes") },
  { id: "round_15", label: t("15 Minutes") },
];

export default function StaffAccountShiftTable(props: IProps) {
  const { userId, addNotes, allowCreate, allowDelete, staffMembers } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { filterHistoryStore, facilityStore, authStore } = useAppSelector(store => store);
  const [staffAccountShifts, setStaffAccountShifts] = useState<Array<IStaffAccountShift>>(undefined);
  const [offset, setOffset] = useState<number>(0);
  const [filters, setFilters] = useState<StaffAccountShiftsFilter>({
    user_ids: filterHistoryStore[SHIFT_USERS_FILTER_KEY]
      ? filterHistoryStore[SHIFT_USERS_FILTER_KEY].currentFilter
      : [],
    start_date: filterHistoryStore[SHIFT_USERS_DATE_KEY]
      ? (filterHistoryStore[SHIFT_USERS_DATE_KEY].currentFilter as string)
      : "",
    end_date: filterHistoryStore[SHIFT_USERS_DATE_KEY]
      ? (filterHistoryStore[SHIFT_USERS_DATE_KEY].currentFilter as string)
      : "",
  });

  const {
    state: addNoteModal,
    updateModal: updateAddNoteModal,
    closeModal: closeAddNoteModal,
  } = useModal<{ shift: IStaffAccountShift }>({ shift: null });
  
  const {
    state: deleteNoteModal,
    updateModal: updateDeleteNoteModal,
    closeModal: closeDeleteNoteModal,
  } = useModal<{ shift: IStaffAccountShift }>({ shift: null });
  const {
    state: deleteShiftModal,
    updateModal: updateDeleteShiftModal,
    closeModal: closeDeleteShiftModal,
  } = useModal<{ shift: IStaffAccountShift }>({ shift: null });
  const {
    state: createShiftModal,
    updateModal: updateCreateShiftModal,
    closeModal: closeCreateShiftModal,
  } = useModal<{
    userId: number | null;
    clockInDate: Date;
    clockInTime: string;
    clockOutDate: Date;
    clockOutTime: string;
    notes: string;
    role_id: number;
    wage: number;
    staff: IFacilityAdmin;
  }>({
    userId: null,
    clockInDate: new Date(),
    clockInTime: "",
    clockOutDate: new Date(),
    clockOutTime: "",
    notes: "",
    role_id: null, 
    wage: null,
    staff: null,
  });
  const {
    state: editShiftModal,
    updateModal: updateEditShiftModal,
    closeModal: closeEditShiftModal,
  } = useModal<{
    shift: IStaffAccountShift & { clockInTime: string; clockOutTime: string; clockInDate: Date; clockOutDate: Date };
    addNote: boolean;
    originalNote: string;
    role_id: number;
    wage: number;
  }>({ shift: null, addNote: false, originalNote: "", role_id: null, wage: null });

  const {
    state: settingsModal,
    updateModal: updateSettingsModal,
    closeModal: closeSettingsModal,
  } = useModal<{
    clock_in_round_option: TClockRoundOption;
    clock_out_round_option: TClockRoundOption;
    clock_in_radius_meters: string;
  }>({ clock_in_round_option: "no_round", clock_out_round_option: "no_round", clock_in_radius_meters: "" });

  const {
    state: previewModal,
    closeModal: closePreviewModal,
    updateModal: updatePreviewModal,
  } = useModal({ html: "" });

  const {
    state: gratuityModal,
    closeModal: closeGratuityModal,
    updateModal: updateGratuityModal,
  } = useModal<{
    dateString: string;
    action: "preview" | "print";
    kitchenPercentage: string;
    barPercentage: string;
    supportPercentage: string;
  }>({
    dateString: "",
    action: "preview",
    kitchenPercentage: "0",
    barPercentage: "0",
    supportPercentage: "0",
  });

  const [openTablesModal, setOpenTablesModal] = useState<{ isOpen: boolean; tables: ITable[] }>({
    isOpen: false,
    tables: [],
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getStaffAccountShifts(source.token);
    return () => source.cancel();
  }, [offset, filters]);

  async function getStaffAccountShifts(cancelToken?: CancelToken) {
    //loader
    if (staffAccountShifts !== undefined) {
      setStaffAccountShifts(undefined);
    }

    const extendedFiltersWanted = [filters].some(value => value.user_ids.length > 0 || value?.start_date?.length > 0);

    const filterKeys = Object.keys(filters) as Array<keyof StaffAccountShiftsFilter>;
    let filtersParam = filterKeys
      .map(key => {
        if (filters[key].length > 0) {
          return {
            label: key,
            value: filters[key],
          };
        }
      })
      .filter(value => value !== undefined);

    if (filters?.start_date?.length !== 0) {
      filtersParam = filtersParam?.concat([
        { label: "start_date", value: [filters?.start_date] },
        { label: "end_date", value: [filters?.end_date] },
      ] as Array<{ label: keyof StaffAccountShiftsFilter; value: any }>);
    }

    const res = await GetStaffAccountShiftsAll(
      {
        user_id: userId ? userId : undefined,
        limit: TABLE_LIMIT,
        offset: offset,
        filters: extendedFiltersWanted ? filtersParam : undefined,
      },
      false,
      cancelToken,
    );
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setStaffAccountShifts([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting staff shifts")));
      return;
    }
    setStaffAccountShifts(res?.data);
  }

  function handleOpenGratuityModal(dateString: string, action: "preview" | "print") {
    updateGratuityModal({
      isOpen: true,
      dateString,
      action,
      kitchenPercentage: "0",
      barPercentage: "0",
      supportPercentage: "0",
    });
  }

  async function submitGratuityModal() {
    const openTables = await getOpenTablesForUser(authStore.user?.id);

    if (openTables.length > 0) {
      setOpenTablesModal({ isOpen: true, tables: openTables });
      return;
    }

    await fetchShiftSummary();
  }

  async function fetchShiftSummary() {
    const kitchenPercentageNumber = Number.isFinite(Number(gratuityModal?.kitchenPercentage))
      ? Number(gratuityModal?.kitchenPercentage)
      : 0;

    const barPercentageNumber = Number.isFinite(Number(gratuityModal?.barPercentage))
      ? Number(gratuityModal?.barPercentage)
      : 0;

    const supportPercentageNumber = Number.isFinite(Number(gratuityModal?.supportPercentage))
      ? Number(gratuityModal?.supportPercentage)
      : 0;

    const shiftSummaryResponse = await GetShifts(
      {
        date: gratuityModal?.dateString,
        kitchen_percent: kitchenPercentageNumber,
        bar_percent: barPercentageNumber,
        support_percent: supportPercentageNumber,
      },
      true,
    );

    if (shiftSummaryResponse.status !== StatusCode.OK) {
      console.log(shiftSummaryResponse.message);
      return;
    }

    if (gratuityModal?.action === "preview") {
      updatePreviewModal({ isOpen: true, html: shiftSummaryResponse.data });
    } else {
      window.open().document.write(shiftSummaryResponse.data as string);
    }

    closeGratuityModal();
  }

  function handleContinueWithOpenTables() {
    setOpenTablesModal({ isOpen: false, tables: [] });
    void fetchShiftSummary();
  }

  function formatTime(timeDifference: moment.Duration) {
    const hours = timeDifference.hours().toString().padStart(2, "0");
    const minutes = timeDifference.minutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  function handleOpenEditModal(shift: IStaffAccountShift) {
    const clockInTime = shift?.clock_in_at?.split(" ")[1];
    const clockOutTime = shift?.clock_out_at ? shift?.clock_out_at?.split(" ")[1] : null;
    const convertedClockInTime = moment.utc(clockInTime, "HH:mm")?.local()?.format("HH:mm");
    let convertedClockOutTime = clockOutTime;

    const clockInDate = new Date(
      moment.utc(shift?.clock_in_at, "YYYY-MM-DD HH:mm")?.local()?.format("YYYY-MM-DD HH:mm")?.toString(),
    );
    const clockOutDate = shift?.clock_out_at
      ? new Date(moment.utc(shift?.clock_out_at, "YYYY-MM-DD HH:mm")?.local()?.format("YYYY-MM-DD HH:mm")?.toString())
      : null;

    if (clockOutTime) {
      convertedClockOutTime = moment.utc(clockOutTime, "HH:mm")?.local()?.format("HH:mm");
    }
    void updateEditShiftModal({
      isOpen: true,
      shift: {
        ...shift,
        clockInTime: convertedClockInTime,
        clockOutTime: convertedClockOutTime,
        clockInDate,
        clockOutDate,
      },
      addNote: shift?.notes ? true : false,
      originalNote: shift?.notes,
      role_id: shift?.role_id,
    });
  }

  async function saveCreateShift() {
    if (!createShiftModal?.userId) {
      dispatch(showError(t("Please select a staff member")));
      return;
    }

    if (!createShiftModal?.clockInTime || !createShiftModal?.clockOutTime) {
      dispatch(showError(t("Please enter clock in and clock out times")));
      return;
    }

    const clockInDate = moment(createShiftModal.clockInDate).format("YYYY-MM-DD");
    const clockInAt = `${clockInDate} ${createShiftModal.clockInTime}`;
    const clockInTimeUtc = moment(clockInAt)?.utc()?.format("YYYY-MM-DD HH:mm:ss");

    const clockOutDate = moment(createShiftModal.clockOutDate).format("YYYY-MM-DD");
    const clockOutAt = `${clockOutDate} ${createShiftModal.clockOutTime}`;
    const clockOutTimeUtc = moment(clockOutAt)?.utc()?.format("YYYY-MM-DD HH:mm:ss");

    if (clockInTimeUtc > clockOutTimeUtc) {
      dispatch(showError(t("Clock in date cannot be after the clock out date")));
      return;
    }

    const res = await PostStaffAccountShift(
      {
        user_id: createShiftModal.userId,
        clock_in_at: clockInTimeUtc,
        clock_out_at: clockOutTimeUtc,
        notes: createShiftModal.notes || undefined,
        role_id: createShiftModal?.role_id,
      },
      true,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error creating shift")));
      return;
    }

    dispatch(showSuccess(t("Successfully created shift")));
    void closeCreateShiftModal();
    void getStaffAccountShifts();
  }

  async function deleteShift() {
    if (deleteShiftModal?.shift) {
      const res = await DeleteStaffAccountShift({ id: deleteShiftModal.shift.id }, true);

      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting shift")));
        return;
      }

      dispatch(showSuccess(t("Successfully deleted shift")));
      void closeDeleteShiftModal();
      void getStaffAccountShifts();
    }
  }

  function handleOpenCreateModal() {
    updateCreateShiftModal({
      isOpen: true,
      userId: null,
      clockInDate: new Date(),
      clockInTime: "",
      clockOutDate: new Date(),
      clockOutTime: "",
      notes: "",
    });
  }

  async function saveEditShift() {
    if (editShiftModal?.shift) {
      const clockInDate = moment(editShiftModal.shift?.clockInDate).format("YYYY-MM-DD");

      const clockInAt = `${clockInDate} ${editShiftModal?.shift?.clockInTime}`;
      const clockInTimeUtc = moment(clockInAt)?.utc()?.format("YYYY-MM-DD HH:mm:ss");
      let clockOutTimeUtc = editShiftModal?.shift?.clockOutTime;

      if (editShiftModal?.shift?.clockOutTime) {
        const clockOutDate = moment(editShiftModal.shift?.clockOutDate).format("YYYY-MM-DD");
        const clockOutAt = `${clockOutDate} ${editShiftModal?.shift?.clockOutTime}`;
        clockOutTimeUtc = moment(clockOutAt)?.utc()?.format("YYYY-MM-DD HH:mm:ss");

        if (clockInTimeUtc > clockOutTimeUtc) {
          dispatch(showError(t("Clock in date cannot be after the clock out date")));
          return;
        }
      }

      const res = await PutStaffAccountShift(
        {
          id: editShiftModal?.shift?.id,
          clock_in_at: clockInTimeUtc,
          clock_out_at: clockOutTimeUtc ?? undefined,
          notes: editShiftModal?.shift?.notes,
          role_id: editShiftModal?.role_id,
        },
        true,
      );

      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error updating shift")));
        return;
      }
      dispatch(showSuccess(t("Successfully updated shift")));
      void closeEditShiftModal();
      void getStaffAccountShifts();
    }
  }

  async function deleteShiftNote() {
    if (deleteNoteModal?.shift) {
      const res = await PutStaffAccountShift(
        {
          id: deleteNoteModal?.shift?.id,
          clock_in_at: deleteNoteModal?.shift?.clock_in_at,
          clock_out_at: deleteNoteModal?.shift?.clock_out_at ?? undefined,
          notes: "",
        },
        true,
      );
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting shift note")));
        return;
      }
      dispatch(showSuccess(t("Successfully deleted shift note")));
      void closeDeleteNoteModal();
      void getStaffAccountShifts();
    }
  }

  async function addShiftNote() {
    if (addNoteModal?.shift) {
      const res = await PutStaffAccountShift(
        {
          id: addNoteModal?.shift?.id,
          clock_in_at: addNoteModal?.shift?.clock_in_at,
          clock_out_at: addNoteModal?.shift?.clock_out_at ?? undefined,
          notes: addNoteModal?.shift?.notes,
        },
        true,
      );
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error adding shift note")));
        return;
      }
      dispatch(showSuccess(t("Successfully added shift note")));
      void closeAddNoteModal();
      void getStaffAccountShifts();
    }
  }

  function handleExtendedFilter(filterReturn: Record<string, any>[], type: keyof StaffAccountShiftsFilter) {
    setFilters(prev => ({
      ...prev,
      [type]: filterReturn.map(filter => filter.id as number),
    }));
  }

  const allowEdit = !userId || (userId && authStore?.user?.permissions?.admin_edit_shift_history);

  const staffList = staffMembers ?? facilityStore?.staffAccounts;

  const staffOptions = useMemo(
    () =>
      staffList?.map(staff => ({
        id: staff.id,
        label: staff.full_name,
        extraValue: staff,
      })) ?? [],
    [staffList],
  );

  function renderStaffOption(option: { id: string | number; label: string | number; extraValue?: IFacilityStaff }) {
    return (
      <div>
        <p className="ui-select_dropdown_text">{option.label}</p>
        {option.extraValue?.email ? <p className="text-sm text-gray-500">{option.extraValue.email}</p> : null}
      </div>
    );
  }

  function formatShiftTime(duration: moment.Duration) {
    const totalDays = Math.floor(duration.asDays());

    if (totalDays >= 1) {
      const days = duration.days();
      const hours = duration.hours();
      const minutes = duration.minutes();

      const parts = [];
      if (days > 0) {
        parts.push(`${days}d`);
      }
      if (hours > 0 || days > 0) {
        parts.push(`${hours}h`);
      }

      parts.push(`${minutes}m`);

      return parts.join(" ");
    } else {
      return formatTime(duration);
    }
  }

  function updateTimeClockSettings() {
    if (
      settingsModal?.clock_in_round_option &&
      settingsModal?.clock_out_round_option &&
      facilityStore?.teeSheetSettings
    ) {
      if (settingsModal?.clock_in_radius_meters && !isNaN(Number(settingsModal?.clock_in_radius_meters))) {
        if (Number(settingsModal?.clock_in_radius_meters) < 0) {
          dispatch(showError(t("Clock in radius must be a positive number")));
          return;
        } else if (Number(settingsModal?.clock_in_radius_meters) === 0) {
          dispatch(showError(t("Clock in radius must be greater than 0")));
          return;
        }
      }
      void dispatch(
        updateFacilityTeeSheetSettings(
          {
            ...facilityStore?.teeSheetSettings,
            clock_in_round_option: settingsModal?.clock_in_round_option,
            clock_out_round_option: settingsModal?.clock_out_round_option,
            clock_in_radius_meters:
              settingsModal?.clock_in_radius_meters !== "" ? Number(settingsModal?.clock_in_radius_meters) : null,
          },
          true,
        ),
      );
      void closeSettingsModal();
    }
  }

  console.log('%csrc/pages/secure/facility/settings/shifts/staffAccountShiftTable/StaffAccountShiftTable.tsx:580 createShiftModal', 'color: #007acc;', createShiftModal);

  return (
    <div>
      {!userId ? (
        <div className="flex items-center gap-2 mt-4 mb-4">
          <DropFilter
            key="date"
            title={t("Date")}
            filterData={[
              {
                start_date: filters?.start_date,
                end_date: filters?.end_date,
                label: t("Date"),
              },
            ]}
            filterLabelPropFromData="label"
            filterIdPropFromData="id"
            filterType="Date Range"
            applyFilters={(filterReturn: Record<string, any>[]) => {
              setFilters(prev => ({
                ...prev,
                start_date: filterReturn[0]?.start_date ?? "",
                end_date: filterReturn[0]?.end_date ?? "",
              }));
              setOffset(0);
            }}
            leftAligned
            save
            historyKey={SHIFT_USERS_DATE_KEY}
          />
          <DropFilter
            title={t("Staff")}
            filterData={staffList}
            filterLabelPropFromData="full_name"
            filterIdPropFromData="id"
            filterType="Checkbox"
            historyKey={SHIFT_USERS_FILTER_KEY}
            applyFilters={filterReturn => handleExtendedFilter(filterReturn, "user_ids")}
            defaultCheckboxes={filters?.user_ids}
          />
        </div>
      ) : null}
      <Card
        title={t("Shift Summary")}
        titleActions={
          !userId
            ? [
                ...(allowCreate
                  ? [
                      {
                        content: t("New Shift"),
                        action: handleOpenCreateModal,
                        buttonType: "primary" as const,
                      },
                    ]
                  : []),
                {
                  content: t("Timeclock Settings"),
                  action: () =>
                    updateSettingsModal({
                      isOpen: true,
                      clock_in_round_option: facilityStore?.teeSheetSettings?.clock_in_round_option,
                      clock_out_round_option: facilityStore?.teeSheetSettings?.clock_out_round_option,
                      clock_in_radius_meters: facilityStore?.teeSheetSettings?.clock_in_radius_meters?.toString() ?? "",
                    }),
                  buttonType: "secondary" as const,
                  icon: <FontAwesomeIcon icon={faClock} />,
                },
              ]
            : undefined
        }
      >
        <Card.Section>
          <DataTable
            columns={[
              { label: t("Date") },
              { label: t("Name") },
              { label: "Start Time" },
              { label: t("End Time") },
              { label: t("Duration") },
              { label: t("Role") },
              { label: t("Note"), width: "20%" },
              { label: "" },
            ]}
            loading={!staffAccountShifts}
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset: offset,
              disableNextOffset: !staffAccountShifts || !(staffAccountShifts?.length === TABLE_LIMIT),
              handleTableOffset: direction =>
                setOffset(prev => (direction === "prev" ? prev - TABLE_LIMIT : prev + TABLE_LIMIT)),
            }}
          >
            {staffAccountShifts?.map((shift, index) => {
              const [date, clockInTime] = shift.clock_in_at.split(" ");
              const clockOut = shift?.clock_out_at ? shift?.clock_out_at?.split(" ") : null;
              const duration = shift?.clock_out_at
                ? moment.duration(moment(shift?.clock_out_at).diff(moment(shift?.clock_in_at)))
                : null;
              return (
                <tr
                  key={index}
                  className={allowEdit ? "clickable" : ""}
                  onClick={!allowEdit ? undefined : () => handleOpenEditModal(shift)}
                >
                  <td>{moment(date, "YYYY-MM-DD")?.format("LL")}</td>
                  <td>{shift?.user?.full_name}</td>
                  <td>{moment.utc(clockInTime, "HH:mm")?.local()?.format("LT")}</td>
                  <td>{clockOut ? moment.utc(clockOut[1], "HH:mm")?.local()?.format("LT") : "--"}</td>
                  <td>{duration ? formatShiftTime(duration) : "--"}</td>
                  <td>{shift?.role?.title}</td>
                  <td className="ui-staff-account-table-note">
                    {shift?.notes ? (
                      shift?.notes
                    ) : allowEdit || addNotes ? (
                      <div
                        className="cursor-pointer"
                        onClick={e => {
                          e.stopPropagation();
                          void updateAddNoteModal({ isOpen: true, shift });
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} size="1x" /> {t("Add Note")}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      {allowEdit ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`title-detail-${index}`}>{t("Edit Shift")}</Tooltip>}
                        >
                          <FontAwesomeIcon icon={faPen} size="1x" />
                        </OverlayTrigger>
                      ) : null}
                      {allowDelete ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`delete-shift-${index}`}>{t("Delete Shift Summary")}</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            size="1x"
                            onClick={e => {
                              e.stopPropagation();
                              void updateDeleteShiftModal({ isOpen: true, shift });
                            }}
                          />
                        </OverlayTrigger>
                      ) : null}
                      {userId === authStore.user?.id ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`title-detail-${index}`}>{t("Print Shift Sales Summary")}</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faFileInvoiceDollar}
                            size="1x"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenGratuityModal(date, "print");
                            }}
                          />
                        </OverlayTrigger>
                      ) : null}
                      {userId === authStore.user?.id ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`title-detail-${index}`}>{t("Preview Shift Sales Summary")}</Tooltip>}
                        >
                          <FontAwesomeIcon
                            icon={faMagnifyingGlassDollar}
                            size="1x"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenGratuityModal(date, "preview");
                            }}
                          />
                        </OverlayTrigger>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Card.Section>
      </Card>
      <Portal isMounted={createShiftModal?.isOpen}>
        <Sheet
          open={createShiftModal?.isOpen}
          size="small"
          title={t("New Shift Summary")}
          okText={t("Save")}
          onCancel={closeCreateShiftModal}
          onOk={saveCreateShift}
          overflow
          closable
        >
          <FormLayout>
            <FormLayout.Group>
              <SelectNew
                label={t("Staff")}
                id="create_shift_staff"
                options={staffOptions}
                value={createShiftModal?.userId}
                onChange={(value, staff: IFacilityAdmin) => updateCreateShiftModal({ userId: Number(value), staff: staff, role_id: null, wage: null })}
                renderOption={renderStaffOption}
                searchable
                placeholder={t("Select staff member")}
              />
            </FormLayout.Group>
            <FormLayout.Group title={<p className="text-black font-semibold">{t("Shift Time: ")}</p>}>
              <DatePickerInput
                label={t("Start Date")}
                months={1}
                position={"center"}
                startingDate={createShiftModal?.clockInDate}
                setStartingDate={selectedDate =>
                  updateCreateShiftModal({ clockInDate: selectedDate as Date })
                }
              />
              <TimePick
                label={t("Start Time")}
                value={createShiftModal?.clockInTime}
                onChange={timeString => updateCreateShiftModal({ clockInTime: timeString })}
                size="medium"
                align="right"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <DatePickerInput
                label={t("End Date")}
                months={1}
                position={"center"}
                startingDate={createShiftModal?.clockOutDate}
                setStartingDate={selectedDate =>
                  updateCreateShiftModal({ clockOutDate: selectedDate as Date })
                }
              />
              <TimePick
                label={t("End Time")}
                value={createShiftModal?.clockOutTime}
                onChange={timeString => updateCreateShiftModal({ clockOutTime: timeString })}
                size="medium"
                align="right"
              />
            </FormLayout.Group>
            <FormLayout.Group>
            <SelectNew
                id={"role_selector"}
                label={t("Role")}
                onChange={(id: number, role: Record<string, any>) => updateCreateShiftModal({role_id: id, wage: role?.wage})}
                value={createShiftModal?.role_id}
                isLoading={!createShiftModal?.staff?.roles}
                options={createShiftModal?.staff?.roles?.map(role => ({ id: role?.id, label: role.title, extraValue: role }))}
                disabled={!createShiftModal?.userId}
              />
            </FormLayout.Group>
            <FormLayout.Group title={<p className="text-black font-semibold">{t("Note: ")}</p>}>
              <TextField
                maxLength={200}
                labelHidden
                placeholder={t("Shift notes...")}
                value={createShiftModal?.notes}
                onChange={e => updateCreateShiftModal({ notes: e.target?.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      <Portal isMounted={deleteShiftModal?.isOpen}>
        <Popup
          open={deleteShiftModal?.isOpen}
          title={t("Delete Shift Summary")}
          description={t("Are you sure you want to delete this shift summary?")}
          type="delete"
          onCancel={closeDeleteShiftModal}
          onOk={deleteShift}
          okText={t("Delete")}
        />
      </Portal>

      <Portal isMounted={editShiftModal?.isOpen}>
        <Sheet
          open={editShiftModal?.isOpen}
          size="small"
          title={
            <>
              {t("Edit Shift for")}:{" "}
              <Badge size="medium" type="gray" iconLeft={<FontAwesomeIcon icon={faUser} />}>
                {editShiftModal?.shift?.user?.full_name}
              </Badge>
            </>
          }
          okText={t("Save & Apply")}
          onCancel={closeEditShiftModal}
          onOk={saveEditShift}
          overflow
          closable
        >
          <FormLayout>
            <FormLayout.Group title={<p className="text-black font-semibold">{t("Shift Time: ")}</p>}>
              <DatePickerInput
                label={t("Start Date")}
                months={1}
                position={"center"}
                startingDate={editShiftModal?.shift?.clockInDate}
                setStartingDate={selectedDate =>
                  updateEditShiftModal({ shift: { ...editShiftModal?.shift, clockInDate: selectedDate as Date } })
                }
              />
              <TimePick
                label={t("Start Time")}
                value={editShiftModal?.shift?.clockInTime}
                onChange={timeString =>
                  updateEditShiftModal({ shift: { ...editShiftModal?.shift, clockInTime: timeString } })
                }
                size="medium"
                align="right"
              />
            </FormLayout.Group>
            {editShiftModal?.shift?.clockOutTime !== null ? (
              <FormLayout.Group>
                <DatePickerInput
                  label={t("End Date")}
                  months={1}
                  position={"center"}
                  startingDate={editShiftModal?.shift?.clockOutDate}
                  setStartingDate={selectedDate =>
                    updateEditShiftModal({ shift: { ...editShiftModal?.shift, clockOutDate: selectedDate as Date } })
                  }
                />
                <TimePick
                  label={t("End Time")}
                  value={editShiftModal?.shift?.clockOutTime}
                  onChange={timeString =>
                    updateEditShiftModal({ shift: { ...editShiftModal?.shift, clockOutTime: timeString } })
                  }
                  size="medium"
                  align="right"
                />
              </FormLayout.Group>
            ) : null}
            <FormLayout.Group>
            <SelectNew
                id={"role_selector"}
                label={t("Role")}
                onChange={(id: number, role: Record<string, any>) => updateEditShiftModal({role_id: id, wage: role?.wage})}
                value={editShiftModal?.role_id}
                isLoading={!editShiftModal?.shift?.admin?.roles}
                options={editShiftModal?.shift?.admin?.roles?.map(role => ({ id: role?.id, label: role.title, extraValue: role }))}
              />
            </FormLayout.Group>
            <FormLayout.Group
              title={
                <div className="flex justify-between items-center">
                  <p className="text-black font-semibold">{t("Note: ")}</p>
                  {editShiftModal?.shift?.notes || editShiftModal?.addNote ? (
                    !editShiftModal?.originalNote ? null : (
                      <Button
                        size="small"
                        type="text"
                        style={{ color: "#B42318" }}
                        onClick={() => {
                          void updateDeleteNoteModal({ isOpen: true, shift: editShiftModal?.shift });
                          void closeEditShiftModal();
                        }}
                      >
                        <FontAwesomeIcon icon={faTrashCan} className="mr-1" /> {t("Delete Note")}
                      </Button>
                    )
                  ) : (
                    <Button type="text" size="small" onClick={() => updateEditShiftModal({ addNote: true })}>
                      <FontAwesomeIcon icon={faPlus} size="1x" className="mr-1" />
                      {t("Add Note")}
                    </Button>
                  )}
                </div>
              }
            >
              {editShiftModal?.shift?.notes || editShiftModal?.addNote ? (
                <TextField
                  maxLength={200}
                  labelHidden
                  placeholder={t("Shift notes...")}
                  value={editShiftModal?.shift?.notes}
                  onChange={e => updateEditShiftModal({ shift: { ...editShiftModal?.shift, notes: e.target?.value } })}
                />
              ) : (
                <Callout
                  title={t("No Note Available")}
                  content={t("This shift doesn't have any note attached")}
                  type="info"
                />
              )}
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      <Portal isMounted={deleteNoteModal?.isOpen}>
        <Popup
          open={deleteNoteModal?.isOpen}
          title={t("Delete Note")}
          description={t("Are you sure you want to delete this note? This cannot be undone.")}
          type="delete"
          onCancel={closeDeleteNoteModal}
          onOk={deleteShiftNote}
          okText={t("Delete Note")}
        />
      </Portal>
      <Portal isMounted={addNoteModal?.isOpen}>
        <Sheet
          open={addNoteModal?.isOpen}
          title={t("Add Shift Note")}
          onCancel={closeAddNoteModal}
          okText={t("Save & Apply")}
          size="small"
          onOk={addShiftNote}
          closable
        >
          <FormLayout>
            <FormLayout.Group>
              <TextField
                maxLength={200}
                labelHidden
                placeholder={t("Shift notes...")}
                value={addNoteModal?.shift?.notes}
                onChange={e => updateAddNoteModal({ shift: { ...addNoteModal?.shift, notes: e.target?.value } })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>
      <Sheet
        title={t("Preview Shift Summary")}
        open={previewModal.isOpen}
        size="small"
        closable
        onCancel={closePreviewModal}
        onOk={() => window.open().document.write(previewModal.html)}
        okText={t("Print")}
      >
        <div dangerouslySetInnerHTML={{ __html: previewModal.html }}></div>
      </Sheet>

      <Portal isMounted={gratuityModal?.isOpen}>
        <Sheet
          title={t("Gratuity Percentages")}
          open={gratuityModal?.isOpen}
          size="small"
          closable
          overflow
          onCancel={closeGratuityModal}
          onOk={submitGratuityModal}
          okText={gratuityModal?.action === "preview" ? t("Preview") : t("Print")}
        >
          <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
            <Input
              id="kitchen_percent"
              label={t("Kitchen Percentage (%)")}
              type="number"
              value={gratuityModal?.kitchenPercentage}
              onChange={event => updateGratuityModal({ kitchenPercentage: event.target.value })}
            />
            <Input
              id="bar_percent"
              label={t("Bar Percentage (%)")}
              type="number"
              value={gratuityModal?.barPercentage}
              onChange={event => updateGratuityModal({ barPercentage: event.target.value })}
            />
            <Input
              id="support_percent"
              label={t("Support Percentage (%)")}
              type="number"
              value={gratuityModal?.supportPercentage}
              onChange={event => updateGratuityModal({ supportPercentage: event.target.value })}
            />
          </div>
        </Sheet>
      </Portal>

      <Portal isMounted={settingsModal?.isOpen}>
        <Sheet
          title={t("Set Time Rounding")}
          open={settingsModal.isOpen}
          size="small"
          closable
          onCancel={closeSettingsModal}
          onOk={updateTimeClockSettings}
          okText={t("Apply Changes")}
          overflow
        >
          <FormLayout>
            <FormLayout.Group>
              <SelectNew
                label={t("Clock-In")}
                id="clock_in_option"
                options={TIME_CLOCK_SETTING_OPTIONS(t)}
                helpText={t("Times will be rounded to the nearest selected interval")}
                value={settingsModal?.clock_in_round_option}
                onChange={value => updateSettingsModal({ clock_in_round_option: value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <SelectNew
                label={t("Clock-out")}
                id="clock_out_option"
                options={TIME_CLOCK_SETTING_OPTIONS(t)}
                value={settingsModal?.clock_out_round_option}
                onChange={value => updateSettingsModal({ clock_out_round_option: value })}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                label={t("Clock-In Radius (meters)")}
                tooltipText={t(
                  "Staff must be within this distance of the facility to clock in. Location accuracy may vary. We recommend at least 50-100 meters. Leave blank to disable location restrictions",
                )}
                type="number"
                value={settingsModal?.clock_in_radius_meters}
                suffix={t("m")}
                placeholder={"100"}
                onChange={e => updateSettingsModal({ clock_in_radius_meters: e.target.value })}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      <OpenTablesWarningPopup
        open={openTablesModal.isOpen}
        tables={openTablesModal.tables}
        onCancel={() => setOpenTablesModal({ isOpen: false, tables: [] })}
        onContinue={handleContinueWithOpenTables}
        continueText={gratuityModal?.action === "preview" ? t("Preview Anyway") : t("Print Anyway")}
      />
    </div>
  );
}
